import { create, type JsonObject } from "@bufbuild/protobuf";
import { FieldMaskSchema, type FieldMask } from "@bufbuild/protobuf/wkt";
import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { PageSchema, PageService, type Page, type Template } from "#gen/pages/v2/page_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import { genericize } from "#lib/protobuf-compat.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

interface TemplateList {
	templates: Template[];
	pageInfo?: PaginationPageInfo;
}

interface PagePreview {
	page?: Page;
	data?: JsonObject;
}

export class PageClient {
	private client: Client<typeof PageService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.pages || "/", PageService, options);
	}

	async getPage(id: string): Promise<Page> {
		try {
			const res = await this.client.getPage({ id });
			return res.page!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createPageTemplate(template: Template): Promise<Template> {
		try {
			const res = await this.client.createTemplate({ ...genericize(template) });
			return res.template!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getPageTemplate(id: string): Promise<Template> {
		try {
			const res = await this.client.getTemplate({ id });
			return res.template!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listPageTemplates(orgId: string, workspaceId: string, pagination?: PaginationArguments): Promise<TemplateList> {
		try {
			const res = await this.client.listTemplates({
				orgId,
				workspaceId,
				pagination,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createPage(page: Page): Promise<Page> {
		try {
			const res = await this.client.createPage({ ...genericize(page) });
			return res.page!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updatePage(page: Page, mask: FieldMask): Promise<Page> {
		try {
			const res = await this.client.updatePage({
				page,
				mask,
			});
			return res.page!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archivePage(id: string): Promise<void> {
		try {
			await this.client.updatePage({
				page: create(PageSchema, {
					id: id,
					state: AssetState.ARCHIVED,
				}),
				mask: create(FieldMaskSchema, {
					paths: ["state"],
				}),
			});
		} catch (e) {
			throw handleError(e);
		}
	}

	async deletePage(id: string): Promise<void> {
		try {
			await this.client.deletePage({ id });
		} catch (e) {
			throw handleError(e);
		}
	}

	async savePage(id: string, config: JsonObject, data: JsonObject): Promise<Page> {
		try {
			const res = await this.client.savePage({ id, config, data });
			return res.page!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async previewPage(templateId: string, templateVersion?: string, config?: JsonObject, options?: CallOptions): Promise<PagePreview> {
		try {
			const res = await this.client.previewPage(
				{
					templateId,
					templateVersion,
					config,
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
